import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getBaseUrl } from '../config';
import { useToast } from './Toast';

const BackupAndRestore = ({ onTablesUpdate }) => {
    return <BackupManager onTablesUpdate={onTablesUpdate} />;
};

const loadBackupOrder = () => {
    try {
        const raw = localStorage.getItem('backupOrder');
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
};

const saveBackupOrder = (names) => {
    try {
        localStorage.setItem('backupOrder', JSON.stringify(names));
    } catch {
        // ignore
    }
};

const BackupManager = ({ onTablesUpdate }) => {
    const [activeTab, setActiveTab] = useState('backup');
    const [backupName, setBackupName] = useState('');
    const [uploadFile, setUploadFile] = useState(null);
    const [backups, setBackups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [deleteBackupModal, setDeleteBackupModal] = useState({ isOpen: false, backupName: '' });
    const [deleteBackupInput, setDeleteBackupInput] = useState('');
    const [deleteBackupLoading, setDeleteBackupLoading] = useState(false);
    const showToast = useToast();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');

    const filteredBackups = useMemo(() => {
        let result = backups;
        const term = searchTerm.trim().toLowerCase();
        if (term) {
            result = result.filter((backup) => backup.name.toLowerCase().includes(term));
        }
        return [...result].sort((a, b) => {
            const cmp = a.name.localeCompare(b.name);
            return sortOrder === 'asc' ? cmp : -cmp;
        });
    }, [backups, searchTerm, sortOrder]);

    const toggleSort = () => {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    };


    const fetchBackups = useCallback(async () => {
        try {
            const response = await fetch(`${getBaseUrl()}/ops/backups`);
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || 'Unable to load backups');
            }
            const data = await response.json();
            let backupList = Array.isArray(data) ? data : [];


            backupList = backupList.map(item =>
                typeof item === 'string'
                    ? { name: item }
                    : { ...item, name: item.name || item.filename || item.file_name }
            );


            const savedOrder = loadBackupOrder();
            if (Array.isArray(savedOrder) && savedOrder.length > 0) {
                const apiMap = new Map(backupList.map(item => [item.name, item]));
                const ordered = [];

                for (const name of savedOrder) {
                    if (apiMap.has(name)) {
                        ordered.push(apiMap.get(name));
                        apiMap.delete(name);
                    }
                }

                const remaining = [];
                for (const item of backupList) {
                    if (apiMap.has(item.name)) {
                        remaining.push(item);
                        apiMap.delete(item.name);
                    }
                }
                const finalList = [...remaining, ...ordered];
                setBackups(finalList);
                saveBackupOrder(finalList.map(i => i.name));
            } else {

                setBackups(backupList);
                saveBackupOrder(backupList.map(i => i.name));
            }
        } catch (error) {
            setBackups([]);
            showToast(`Error: ${error.message}`, 'error');
        }
    }, [showToast]);

    useEffect(() => {
        fetchBackups();
    }, [fetchBackups]);

    const handleApiError = async (response) => {
        const errorData = await response.json().catch(() => null);
        if (response.status === 409) {
            const message = errorData?.message || 'A backup with this name already exists';
            return message;
        }
        return errorData?.message || `Request failed with status ${response.status}`;
    };

    const createBackup = async () => {
        if (!backupName.trim()) {
            showToast('Error: Please provide a backup file name.', 'error');
            return;
        }

        setIsLoading(true);

        try {
            const fileName = backupName.endsWith('.json') ? backupName : `${backupName}.json`;
            const response = await fetch(`${getBaseUrl()}/ops/save?file=backups/${encodeURIComponent(fileName)}`, {
                method: 'GET'
            });

            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                showToast(data.message || `Backup created: ${fileName}`, 'success');
                setBackupName('');
                setBackups(prev => {
                    const newArr = [{ name: fileName }, ...prev];
                    saveBackupOrder(newArr.map(i => i.name));
                    return newArr;
                });
            } else {
                const errorMessage = await handleApiError(response);
                showToast(`Error: ${errorMessage}`, 'error');
            }
        } catch (error) {
            showToast(`Error: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const uploadBackup = async () => {
        if (!uploadFile) {
            showToast('Error: Please choose a file to upload.', 'error');
            return;
        }

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('file', uploadFile);

            const response = await fetch(`${getBaseUrl()}/ops/backups`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                const fileName = uploadFile.name;

                showToast(data.message || `Uploaded ${fileName}`, 'success');
                setUploadFile(null);
                document.getElementById('backup-upload-input')?.value && (document.getElementById('backup-upload-input').value = '');
                setBackups(prev => {
                    const newArr = [{ name: fileName }, ...prev.filter(item => item.name !== fileName)];
                    saveBackupOrder(newArr.map(i => i.name));
                    return newArr;
                });
            } else {
                const errorMessage = await handleApiError(response);
                showToast(`Error: ${errorMessage}`, 'error');
            }
        } catch (error) {
            showToast(`Error: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const restoreBackup = async (fileName) => {
        setIsLoading(true);

        try {
            const response = await fetch(`${getBaseUrl()}/ops/load?file=backups/${encodeURIComponent(fileName)}`, {
                method: 'GET'
            });

            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                showToast(data.message || `Restore started for ${fileName}`, 'success');
                // Restoring a backup may create/alter tables, so refresh the tables list
                if (onTablesUpdate) {
                    onTablesUpdate();
                }
            } else {
                const errorMessage = await handleApiError(response);
                showToast(`Error: ${errorMessage}`, 'error');
            }
        } catch (error) {
            showToast(`Error: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const renameBackup = async (oldName) => {
        const newName = window.prompt('Enter the new backup file name', oldName);
        if (!newName || !newName.trim()) {
            return;
        }

        setIsLoading(true);

        try {
            const payload = {
                old_name: oldName,
                new_name: newName
            };
            const response = await fetch(`${getBaseUrl()}/ops/backups/rename`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                showToast(data.message || `Renamed ${oldName} to ${newName}`, 'success');
                setBackups(prev => {
                    const newArr = prev.map(item => item.name === oldName ? { ...item, name: newName } : item);
                    saveBackupOrder(newArr.map(i => i.name));
                    return newArr;
                });
            } else {
                const errorMessage = await handleApiError(response);
                showToast(`Error: ${errorMessage}`, 'error');
            }
        } catch (error) {
            showToast(`Error: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteBackupClick = (fileName) => {
        setDeleteBackupModal({ isOpen: true, backupName: fileName });
        setDeleteBackupInput('');
    };

    const handleDeleteBackupConfirm = async () => {
        if (deleteBackupInput !== deleteBackupModal.backupName) {
            showToast('Error: Backup name does not match', 'error');
            return;
        }

        setDeleteBackupLoading(true);

        try {
            const response = await fetch(`${getBaseUrl()}/ops/backups/${encodeURIComponent(deleteBackupModal.backupName)}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                const data = await response.json().catch(() => ({}));
                showToast(data.message || `Deleted ${deleteBackupModal.backupName}`, 'success');
                setBackups(prev => {
                    const newArr = prev.filter(item => item.name !== deleteBackupModal.backupName);
                    saveBackupOrder(newArr.map(i => i.name));
                    return newArr;
                });
                setDeleteBackupModal({ isOpen: false, backupName: '' });
                setDeleteBackupInput('');
            } else {
                const errorMessage = await handleApiError(response);
                showToast(`Error: ${errorMessage}`, 'error');
            }
        } catch (error) {
            showToast(`Error: ${error.message}`, 'error');
        } finally {
            setDeleteBackupLoading(false);
        }
    };

    const handleDeleteBackupCancel = () => {
        setDeleteBackupModal({ isOpen: false, backupName: '' });
        setDeleteBackupInput('');
    };

    const downloadBackup = async (fileName) => {
        setIsLoading(true);

        try {
            const response = await fetch(`${getBaseUrl()}/ops/backups/${encodeURIComponent(fileName)}`, {
                method: 'GET'
            });

            if (!response.ok) {
                const errorMessage = await handleApiError(response);
                showToast(`Error: ${errorMessage}`, 'error');
                return;
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
            showToast(`Download started for ${fileName}`, 'success');
        } catch (error) {
            showToast(`Error: ${error.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const renderBackupList = () => (
        <div className="backup-file-list">
            <h3 className="title is-5">Backup Files</h3>
            <div className="backup-list-toolbar">
                <div className="field backup-search-field">
                    <div className="control has-icons-left">
                        <input
                            className="input is-small"
                            type="text"
                            placeholder="Search backups..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="icon is-small is-left">
                            <i className="bi bi-search"></i>
                        </span>
                    </div>
                </div>
                <button
                    type="button"
                    className="button is-small"
                    onClick={toggleSort}
                    title={sortOrder === 'asc' ? 'Sort descending' : 'Sort ascending'}
                >
                    <span className="icon is-small">
                        <i className={`bi ${sortOrder === 'asc' ? 'bi-sort-alpha-down' : 'bi-sort-alpha-up'}`}></i>
                    </span>
                    <span>Name</span>
                </button>
            </div>
            {backups.length === 0 ? (
                <div className="notification is-light">No backup files available.</div>
            ) : filteredBackups.length === 0 ? (
                <div className="notification is-light">No backup files match your search.</div>
            ) : (
                filteredBackups.map((backup) => (
                    <div key={backup.name} className="box backup-file-row">
                        <div className="backup-file-details">
                            <div className="backup-file-name">{backup.name}</div>
                        </div>
                        <div className="buttons backup-button-group">
                            {backup.name.toLowerCase().endsWith('.json') && (
                                <button type="button" className="backup-icon-button" title="Restore" onClick={() => restoreBackup(backup.name)} disabled={isLoading}>
                                    <i className="bi bi-arrow-counterclockwise"></i>
                                </button>
                            )}
                            <button type="button" className="backup-icon-button" title="Rename" onClick={() => renameBackup(backup.name)} disabled={isLoading}>
                                <i className="bi bi-pencil"></i>
                            </button>
                            <button type="button" className="backup-icon-button" title="Download" onClick={() => downloadBackup(backup.name)} disabled={isLoading}>
                                <i className="bi bi-download"></i>
                            </button>
                            <button type="button" className="backup-icon-button" title="Delete" onClick={() => handleDeleteBackupClick(backup.name)} disabled={deleteBackupLoading}>
                                <i className="bi bi-trash"></i>
                            </button>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    return (
        <div className="backup-manager-container box">
            <h2 className="title is-4">Backup Manager</h2>
            <div className="backup-manager-tabs">
                <button
                    type="button"
                    className={`button backup-tab-button ${activeTab === 'backup' ? 'active' : 'inactive'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setActiveTab('backup');
                    }}
                >
                    Backup
                </button>
                <button
                    type="button"
                    className={`button backup-tab-button ${activeTab === 'upload' ? 'active' : 'inactive'}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setActiveTab('upload');
                    }}
                >
                    Upload File
                </button>
            </div>

            {activeTab === 'backup' && (
                <div className="backup-tab-content">
                    <div className="backup-input-group">
                        <input
                            type="text"
                            className="input backup-input-field"
                            placeholder="file name to backup"
                            value={backupName}
                            onChange={(e) => setBackupName(e.target.value)}
                            disabled={isLoading}
                        />
                        <button
                            type="button"
                            className={`button is-danger ${isLoading ? 'is-loading' : ''}`}
                            onClick={createBackup}
                            disabled={isLoading}
                        >
                            Backup
                        </button>
                    </div>
                    {renderBackupList()}
                </div>
            )}

            {activeTab === 'upload' && (
                <div className="backup-tab-content">
                    <div className="backup-input-group">
                        <input
                            type="text"
                            className="input backup-input-field upload-field"
                            placeholder="Select a file to upload"
                            value={uploadFile?.name || ''}
                            disabled={true}
                            readOnly
                        />
                        <input
                            id="backup-upload-input"
                            type="file"
                            onChange={(e) => {
                                setUploadFile(e.target.files?.[0] || null);
                                e.stopPropagation();
                            }}
                            disabled={isLoading}
                            className="backup-hidden-file-input"
                        />
                        <button
                            type="button"
                            className="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                document.getElementById('backup-upload-input')?.click();
                            }}
                            disabled={isLoading}
                        >
                            Choose File
                        </button>
                        <button
                            type="button"
                            className={`button is-danger ${isLoading ? 'is-loading' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                uploadBackup();
                            }}
                            disabled={isLoading}
                        >
                            Upload File
                        </button>
                    </div>
                    {renderBackupList()}
                </div>
            )}

            { }
            <div className={`modal ${deleteBackupModal.isOpen ? 'is-active' : ''}`}>
                <div className="modal-background" onClick={handleDeleteBackupCancel}></div>
                <div className="modal-card">
                    <header className="modal-card-head">
                        <p className="modal-card-title">Delete Backup</p>
                        <button
                            className="delete"
                            aria-label="close"
                            onClick={handleDeleteBackupCancel}
                        ></button>
                    </header>
                    <section className="modal-card-body">
                        <p>Are you sure you want to delete the backup <strong>"{deleteBackupModal.backupName}"</strong>?</p>
                        <div className="notification is-danger is-light">
                            <strong>Warning:</strong> This will permanently delete the backup and all associated backup data. This action cannot be undone.
                        </div>
                        <div className="field">
                            <label className="label">
                                Type the backup name <strong>"{deleteBackupModal.backupName}"</strong> to confirm:
                            </label>
                            <div className="control">
                                <input
                                    className="input"
                                    type="text"
                                    placeholder={`Type "${deleteBackupModal.backupName}" here`}
                                    value={deleteBackupInput}
                                    onChange={(e) => setDeleteBackupInput(e.target.value)}
                                    disabled={deleteBackupLoading}
                                />
                            </div>
                        </div>
                    </section>
                    <footer className="modal-card-foot">
                        <button
                            className={`button is-danger ${deleteBackupLoading ? 'is-loading' : ''}`}
                            onClick={handleDeleteBackupConfirm}
                            disabled={deleteBackupLoading || deleteBackupInput !== deleteBackupModal.backupName}
                        >
                            Delete Backup
                        </button>
                        <button
                            className="button"
                            onClick={handleDeleteBackupCancel}
                            disabled={deleteBackupLoading}
                        >
                            Cancel
                        </button>
                    </footer>
                </div>
            </div>
        </div>
    );
};

export default BackupAndRestore;
